/**
 * App bootstrap: Pinia, vue-i18n, router; wires the API client (CSRF token, 401 → login, loading
 * bar), the element SDK's CSRF provider, socket.io lifecycle, preferences (locale, appearance,
 * shortcut remapping) and the service worker.
 */
import { LINK_HOST, setCsrfTokenProvider } from '@creator/plugins-sdk/client';
import { createPinia } from 'pinia';
import { createApp, watch } from 'vue';
import App from './App.vue';
import { api } from './api/client';
import { applyAppearance } from './composables/useColorMode';
import { composer, dictionaryLoader, i18n, resolveLocale, setLocale } from './i18n';
import { registerServiceWorker } from './pwa/register';
import { createAppRouter } from './router';
import { linkHost, setLinkRouter } from './services/links';
import { trackLoading } from './services/notify';
import { realtime } from './services/realtime';
import { shortcuts } from './services/shortcuts';
import { useNavigationStore } from './stores/navigation';
import { usePreferencesStore } from './stores/preferences';
import { useSessionStore } from './stores/session';
import './styles/main.scss';

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);
app.use(i18n);

const session = useSessionStore(pinia);
const prefs = usePreferencesStore(pinia);
const nav = useNavigationStore(pinia);
const router = createAppRouter();

/** The session ended on the server (expired, logged out elsewhere): back to the login page. */
function sessionLost(): void {
  if (!session.authenticated) return;
  session.clear();
  const current = router.currentRoute.value;
  if (!current.meta.public) void router.push({ name: 'login', query: { redirect: current.fullPath, expired: '1' } });
}

// CSRF: the per-session token comes with /auth/login and /auth/me (no cookie for it); the
// element addons' elementApi uses the same source
api.configure({ csrfToken: () => session.csrfToken, onUnauthorized: sessionLost, onActivity: trackLoading });
setCsrfTokenProvider(() => session.csrfToken);
realtime.onAuthExpired = sessionLost;

// locale: preference → browser; UI texts are bundled, legacy dictionaries load in the background
const locale = () => resolveLocale(prefs.values.lang, navigator.languages);
composer.locale.value = locale();
void setLocale(locale());
watch(locale, (l) => void setLocale(l));

applyAppearance();
shortcuts.attach(window);
watch(
  () => prefs.values.shortcuts,
  (o) => shortcuts.setOverrides(o ?? {}),
  { immediate: true, deep: true },
);

watch(
  () => session.status,
  (status, prev) => {
    if (status === 'authenticated') {
      void prefs.sync();
      void nav.loadUserdata();
      void realtime.connect();
    } else if (status === 'anonymous' && prev === 'authenticated') {
      realtime.disconnect();
      nav.reset();
      prefs.reset();
      dictionaryLoader.reset();
      void setLocale(locale());
    }
  },
);

app.use(router);
setLinkRouter(router);
app.provide(LINK_HOST, linkHost);
app.mount('#app');
registerServiceWorker();
